import type { AdminRefundListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { RefundsFilterBar } from './_components/RefundsFilterBar';
import { buildRefundColumns } from './_lib/refundColumns';
import { hasActiveFilters, refundFiltersSchema } from './_lib/refundFilters';
import { useAdminRefundsList } from '../../api/admin-refunds';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/refunds` — admin refunds HISTORY list (AUT-129 / ADMIN-17).
 *
 * **Read-only** (Igor decision A): no issue button here. The
 * order-initiated "Оформить возврат" action lives on the order detail's
 * PaymentTab — this page never imports it. The list is built to the
 * `AdminRefundListItemDto` WIRE (architect guardrail #1), so the mockup's
 * phantom `№ R-####` + `Обработал` columns are absent (no wire field).
 *
 * Gated on `process_payouts` (the AUT-118 controller permission). The
 * recipient-name column renders via `<RedactedField perm="view_pii">`,
 * which agrees with the server's `piiRevealed` flag (refunds are
 * customer-side — the recipient is the paying customer).
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * `useCursorPagination`. No default reason filter — it's a history list,
 * so the operator lands on everything.
 *
 * No row click / detail drawer — there is no refund-detail surface (the
 * refund is read-only history; the actor lives in the audit log). Clones
 * `PayoutsListPage`'s primitive stack minus the selection + drawer.
 */
const RefundsListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(refundFiltersSchema);
  const pager = useCursorPagination();

  // Any filter change resets pagination to the first page (the cursor
  // space is only valid for the filter set that produced it).
  const filterKey = `${(filters.reason ?? []).join(',')}|${filters.orderId ?? ''}|${
    filters.dateFrom ?? ''
  }|${filters.dateTo ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminRefundsList({
    ...(filters.reason && filters.reason.length > 0 ? { reason: filters.reason } : {}),
    ...(filters.orderId ? { orderId: filters.orderId } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildRefundColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('refunds.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('refunds.subtitle')}
        </Typography>
      </Box>

      <RefundsFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminRefundListItemDto>
        rows={items}
        columns={columns}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('refunds.empty')}
          </Typography>
        }
      />
    </Box>
  );
};

const Observed = observer(RefundsListPageInner);

/** Whole-page gate: no `process_payouts` → the default 403 panel. */
export const RefundsListPage = (): ReactElement => (
  <RequirePermission perm="process_payouts">
    <Observed />
  </RequirePermission>
);
